/**
 * Travel requests (R-P10, `KEKA_VS_REIMBURSEMENT.md` gap row 26), extended for the Travel Desk
 * (ADR 0030, D36: extend R-P10, do not replace it). The traveller's side: draft, edit, submit,
 * withdraw, group invitations and the traveller's own profile; the approver's side: approve, reject,
 * ask and answer a question. The desk's side is `travel-desk.contract.ts`, the policy admin
 * `travel-policy.contract.ts`, the reports `travel-report.contract.ts`. R-P10's own `bookingStatus`
 * tracker (`updateBooking`) is kept as shipped.
 *
 * NEVER GATES OR AUTO-CREATES AN EXPENSE. `expense.contract.ts`'s `createExpenseClaimBodySchema`/
 * `createExpenseAdvanceBodySchema` both gain an optional `travelRequestId` — purely a reporting tag
 * ("show me everything this trip cost"), the same one-directional, optional-link shape
 * `ExpenseClaim.advanceId` already uses for a very different reason (net-off, not reporting). This
 * contract has no route that reads or writes those two fields; that lives on the claim/advance
 * side, unchanged.
 *
 * MODULE SWITCH (D33). With `TravelSettings.enabled` off every route here answers 404
 * `module_disabled`. Organisations that already had travel requests when the switch shipped are
 * switched on by a migration, so R-P10 behaves as before for them. The navigation asks
 * `travelPolicy.getModuleStatus` (`GET /travel/admin/status`, any signed-in user) first.
 *
 * ERROR CODES. A 409 or 422 carries `details.code`, one of `TRAVEL_ERROR_CODES`
 * (`travel/error-codes.ts`); a screen branches on the code, never on the message:
 *   - `module_disabled` (404): the module is off for this organisation.
 *   - `stale_version` (409): the `version` sent is not the row's current one. Every edit sends it
 *     and every status change bumps it. Re-read and retry.
 *   - `duplicate_trip` (409): the traveller already has a live trip to the same primary
 *     destination over the same days (same start, same end, or one inside the other), one-way or
 *     round trip alike. A staggered partial overlap is only a warning.
 *   - A malformed trip id in the path is a 422 (`VALIDATION_FAILED`), never a 500.
 *   - `illegal_trip_transition` (409): the status move is not allowed (`TRIP_TRANSITIONS`); the
 *     details carry `from` and `to`.
 *   - `start_in_past` (422): the trip starts before today in the traveller's location timezone and
 *     `isPostFacto` is false.
 *   - `post_facto_not_started` (422): `isPostFacto` is true but the trip starts today or later.
 *     `isPostFacto` is therefore truthful both ways: a trip already begun must be filed as
 *     post-facto and one not yet begun cannot be. A post-facto trip skips the desk and goes
 *     straight to settling once approved.
 *   - `post_facto_too_old` (422): the post-facto trip began longer ago than the policy allows.
 *   - `end_before_start`, `justification_required`, `personal_day_outside_trip` (422).
 *   - `invalid_co_traveller` (422): a co-traveller is unknown, inactive, the requester, or repeated.
 *   - `invalid_leave_request` (422): a personal day names a leave request that is not the traveller's.
 *   - `visa_not_found` (422): a profile update names a visa id that is not on the caller's profile.
 *   - `personal_day_leave_required` (422): submit refuses while a personal day has no leave covering
 *     the whole day; `details.dates` lists the days without leave.
 *   The policy contract adds `policy_overlap` and `policy_in_force`. The traveller-side booking
 *   routes below add the desk codes (`TRAVEL_DESK_ERROR_CODES`): `booking_not_open`,
 *   `booking_on_hold`, `trip_not_open`, `option_not_selected`, `amendment_pending`,
 *   `amendment_not_pending`, `self_booking_not_allowed`, `unknown_itinerary_item`, `empty_proposal`,
 *   `bad_end_date`.
 *
 * APPROVAL CONTEXT. Every route that returns a trip may carry `approval` beside it: the soft policy
 * violations, who the approval is waiting on and the current question thread
 * (`travelApprovalContextSchema`). Optional, so a client that does not read it is unaffected.
 *
 * TRAVELLER SIDE OF THE DESK. After approval the traveller picks an option the desk shared
 * (`selectOption`), settles an item the desk could not book (`resolveItem`), asks for a change or
 * cancels (`requestAmendment`, `cancelTrip`), follows it (`listAmendments`, `withdrawAmendment`,
 * `decideAmendment` for the approver), and reads the trip's bookings, tickets, timeline and group
 * (`listTripBookings`, `listTripDocuments`, `downloadTripDocument`, `getTimeline`, `getGroupRoster`).
 * `getApplicablePolicy` feeds the New Trip form's live policy check.
 *
 * GROUPS. `groupId` is generated by the server for a trip and its co-travellers' invited trips; a
 * client cannot choose it (it would let a caller drop a trip into someone else's group).
 * `coTravellerPersonIds` holds at most 10 distinct people other than the requester.
 *
 * IDEMPOTENCY. `createRequest` and every state-changing POST or PUT below take an
 * `Idempotency-Key` header: a retried request returns the original result instead of a second trip
 * and a second round of invitations. Edits (PATCH) are protected by `version` instead.
 *
 * PII. The traveller profile carries passport and visa numbers on its way IN. Both profile routes
 * are `ai: 'hidden'` so the copilot never reads a profile or records a passport number as a
 * proposal (invariants 7 and 9). Responses only ever carry masked numbers; the one route that
 * decrypts them is the desk's audited `revealTraveller`.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  travelBookingStatusSchema,
  uuidSchema,
} from '../schemas/common.js';
import {
  travelTripStatusSchema,
  travelTripTypeSchema,
  travelJourneyTypeSchema,
  travelBookingModeSchema,
  travelLegSchema,
  travelLegInputSchema,
  travelStaySchema,
  travelStayInputSchema,
  travelCostAllocationSchema,
  travelPersonalDaySchema,
  travelEstimateLineSchema,
  travelFrequentFlyerSchema,
  travelVersionSchema,
  travelApplicablePolicyQuerySchema,
  travelApplicablePolicySchema,
  travelApprovalContextSchema,
  travelAmendmentSchema,
  travelTimelineEventSchema,
  travelGroupRosterSchema,
  tripBookingSchema,
  tripDocumentSchema,
  selectTravelOptionBodySchema,
  resolveTravelItemBodySchema,
  raiseTravelAmendmentBodySchema,
  decideTravelAmendmentBodySchema,
  cancelTravelTripBodySchema,
} from '../travel/schemas.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({
  'idempotency-key': idempotencyKeySchema,
});

const tripParamsSchema = z.object({ travelRequestId: uuidSchema });
const bookingParamsSchema = tripParamsSchema.extend({ bookingId: uuidSchema });
const optionParamsSchema = bookingParamsSchema.extend({ optionId: uuidSchema });
const amendmentParamsSchema = tripParamsSchema.extend({ amendmentId: uuidSchema });
const documentParamsSchema = tripParamsSchema.extend({ documentId: uuidSchema });

/** One file the traveller downloads: the same shape the desk's `downloadDocument` answers. */
export const travelTripDocumentDownloadSchema = z.object({
  fileName: z.string(),
  mimeType: z.string(),
  contentBase64: z.string(),
});

/** 12-status lifecycle (trip-status.ts, TD-2.19): invited, draft, pending, approved, needs_action,
 * booked, rejected, withdrawn, cancelled, expired, settling, closed. Fully backwards-compatible
 * with R-P10's original 5 statuses. */
export const travelRequestStatusSchema = travelTripStatusSchema;

export const travelRequestSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  reference: z.string().nullable(),
  personId: uuidSchema,
  personName: z.string(),
  destination: z.string(),
  purpose: z.string(),
  tripStartDate: dateOnlySchema,
  tripEndDate: dateOnlySchema,
  /** A budgeting figure, never a cap and never charged against anything. Null when the requester
   * doesn't know the cost yet. */
  estimatedCost: decimalStringSchema.nullable(),
  currency: currencySchema,
  status: travelRequestStatusSchema,
  /** Deliberately not a foreign key — same F12 reasoning as `ExpenseClaim.workflowInstanceId`. */
  workflowInstanceId: uuidSchema.nullable(),
  submittedAt: instantSchema.nullable(),
  bookingStatus: travelBookingStatusSchema,
  bookingReference: z.string().nullable(),
  bookingNote: z.string().nullable(),
  /** Extended Travel Desk fields (TD-2.19) */
  tripType: travelTripTypeSchema.default('domestic'),
  journeyType: travelJourneyTypeSchema.default('one_way'),
  bookingMode: travelBookingModeSchema.default('desk'),
  isPostFacto: z.boolean().default(false),
  justification: z.string().nullable().optional(),
  version: z.number().int().default(1),
  groupId: uuidSchema.nullable().optional(),
  invitedById: uuidSchema.nullable().optional(),
  legs: z.array(travelLegSchema).optional(),
  stays: z.array(travelStaySchema).optional(),
  costAllocations: z.array(travelCostAllocationSchema).optional(),
  personalDays: z.array(travelPersonalDaySchema).optional(),
  estimateLines: z.array(travelEstimateLineSchema).optional(),
  /**
   * The approver's view beside the trip (soft violations, whose decision it waits on, the open
   * question thread). Present on every route that returns a trip; see `travelApprovalContextSchema`.
   */
  approval: travelApprovalContextSchema.optional(),
  /** How many expense claims currently carry this trip's id, purely informational — the same
   * "count only, fetch lines separately" shape `expenseReportSchema.lineCount` uses. */
  linkedClaimCount: z.number().int().nonnegative(),
  linkedAdvanceCount: z.number().int().nonnegative(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});

export const listTravelRequestsQuerySchema = paginationQuerySchema.extend({
  /** Defaults to the caller's own requests. Anyone else's requires `travel:approve` over them. */
  personId: uuidSchema.optional(),
  status: travelRequestStatusSchema.optional(),
  /** Free text over reference, destination and purpose. */
  q: z.string().trim().min(1).max(120).optional(),
});

/** A personal day as the client sends it: the leave request is optional (submit refuses without one, 422 `personal_day_leave_required`). */
const travelPersonalDayInputSchema = travelPersonalDaySchema.extend({
  leaveRequestId: uuidSchema.nullable().optional(),
});

/** Up to 10 distinct co-travellers. That the requester is not one of them is checked by the route. */
const coTravellerPersonIdsSchema = z
  .array(uuidSchema)
  .max(10)
  .refine((ids) => new Set(ids).size === ids.length, {
    message: 'A co-traveller can be listed once',
  });

export const createTravelRequestBodySchema = z
  .object({
    destination: z.string().trim().min(1).max(200),
    purpose: z.string().trim().min(1).max(1000),
    tripStartDate: dateOnlySchema,
    tripEndDate: dateOnlySchema,
    estimatedCost: decimalStringSchema.optional(),
    currency: currencySchema,
    tripType: travelTripTypeSchema.optional().default('domestic'),
    journeyType: travelJourneyTypeSchema.optional().default('one_way'),
    bookingMode: travelBookingModeSchema.optional().default('desk'),
    /** Must match the dates: true exactly when the trip has already started (422 otherwise). */
    isPostFacto: z.boolean().optional().default(false),
    justification: z.string().trim().max(1000).nullable().optional(),
    /** People to invite as travellers; each gets their own `invited` trip in the same server-made group. */
    coTravellerPersonIds: coTravellerPersonIdsSchema.optional(),
    legs: z.array(travelLegInputSchema).max(50).optional(),
    stays: z.array(travelStayInputSchema).max(50).optional(),
    costAllocations: z.array(travelCostAllocationSchema).max(20).optional(),
    personalDays: z.array(travelPersonalDayInputSchema).max(366).optional(),
  })
  .refine((body) => body.tripStartDate <= body.tripEndDate, {
    message: 'tripStartDate must be on or before tripEndDate',
    path: ['tripEndDate'],
  });

/**
 * Edit a draft. `version` is required (the row's current one, from the last read): the write is a
 * compare-and-set, and a stale token is 409 `stale_version`, so two edits can never interleave. A
 * field left out is unchanged; a list that is sent REPLACES that list.
 */
export const updateTravelRequestBodySchema = z
  .object({
    version: travelVersionSchema,
    destination: z.string().trim().min(1).max(200).optional(),
    purpose: z.string().trim().min(1).max(1000).optional(),
    tripStartDate: dateOnlySchema.optional(),
    tripEndDate: dateOnlySchema.optional(),
    estimatedCost: decimalStringSchema.nullable().optional(),
    currency: currencySchema.optional(),
    tripType: travelTripTypeSchema.optional(),
    journeyType: travelJourneyTypeSchema.optional(),
    bookingMode: travelBookingModeSchema.optional(),
    isPostFacto: z.boolean().optional(),
    justification: z.string().trim().max(1000).nullable().optional(),
    /**
     * People to invite to this draft, the same rules as `createRequest` (422
     * `invalid_co_traveller`). ADDITIVE, unlike the lists below: someone already in the trip's
     * group is skipped (never invited twice), and a person left out is NOT un-invited. Only the
     * trip's organiser (a trip nobody invited) may add people. Lets an autosaved draft keep its
     * co-travellers (bug 6).
     */
    coTravellerPersonIds: coTravellerPersonIdsSchema.optional(),
    legs: z.array(travelLegInputSchema).max(50).optional(),
    stays: z.array(travelStayInputSchema).max(50).optional(),
    costAllocations: z.array(travelCostAllocationSchema).max(20).optional(),
    personalDays: z.array(travelPersonalDayInputSchema).max(366).optional(),
  })
  .refine(
    (body) =>
      body.tripStartDate === undefined ||
      body.tripEndDate === undefined ||
      body.tripStartDate <= body.tripEndDate,
    { message: 'tripStartDate must be on or before tripEndDate', path: ['tripEndDate'] },
  );

/**
 * Submit a draft. `version` is optional: when sent it must be the draft's current version (409
 * `stale_version`), so a submit cannot go out on a draft that was edited from another tab since
 * the client last read it.
 */
export const submitTravelRequestBodySchema = z.object({
  version: travelVersionSchema.optional(),
});

export const withdrawTravelRequestBodySchema = z.object({
  reason: z.string().trim().max(500).optional(),
});

export const decideTravelRequestBodySchema = z.object({
  comment: z.string().max(500).optional(),
});

/** The "travel desk" action — settable only once the request is `approved` (enforced at the
 * route). `booked`/`cancelled` both accept a reference/note; `not_booked` clears both, the way an
 * approver would use it to undo a booking recorded by mistake. */
export const updateTravelBookingBodySchema = z.object({
  bookingStatus: travelBookingStatusSchema,
  bookingReference: z.string().trim().max(200).optional(),
  bookingNote: z.string().trim().max(1000).optional(),
});

export const travellerVisaViewSchema = z.object({
  id: uuidSchema,
  country: z.string().length(2),
  visaType: z.string(),
  /** Masked to the last four characters; the full number is only ever the desk's audited reveal. */
  maskedNumber: z.string().nullable(),
  validFrom: dateOnlySchema,
  validTo: dateOnlySchema,
  entries: z.string(),
});

/**
 * THE traveller profile: the one shape the routes, the web and the desk reveal share. Numbers are
 * masked here, and a profile is created empty on first read. `version` is the token
 * `updateProfile` sends back.
 */
export const travellerProfileViewSchema = z.object({
  id: uuidSchema,
  personId: uuidSchema,
  nameAsOnId: z.string().nullable(),
  hasPassport: z.boolean(),
  maskedPassportNumber: z.string().nullable(),
  passportCountry: z.string().nullable(),
  passportExpiry: dateOnlySchema.nullable(),
  /** Free text ("vegetarian", "jain"): a preference, not a rule anything branches on. */
  mealPreference: z.string().nullable(),
  seatPreference: z.string().nullable(),
  frequentFlyer: z.array(travelFrequentFlyerSchema).nullable(),
  visas: z.array(travellerVisaViewSchema),
  version: travelVersionSchema,
  updatedAt: instantSchema,
});
export type TravellerProfileView = z.infer<typeof travellerProfileViewSchema>;

/**
 * One visa in a profile update. The `visas` list REPLACES the stored set, matched by id:
 *   - an entry WITH `id` updates that visa (422 `visa_not_found` when it is not on the caller's profile);
 *   - an entry WITHOUT `id` adds a new visa;
 *   - a stored visa whose id is not in the list is deleted;
 *   - on an existing visa, `number` omitted KEEPS the stored (encrypted) number, `number: null`
 *     erases it, and a string replaces it. A client editing dates or type never has to resend a
 *     number it cannot see.
 */
export const updateTravellerVisaInputSchema = z
  .object({
    id: uuidSchema.optional(),
    country: z.string().trim().length(2),
    visaType: z.string().trim().min(1).max(50),
    number: z.string().trim().min(1).max(50).nullable().optional(),
    validFrom: dateOnlySchema,
    validTo: dateOnlySchema,
    entries: z.enum(['single', 'double', 'multiple']).default('multiple'),
  })
  .refine((visa) => visa.validTo >= visa.validFrom, {
    message: 'The visa ends before it starts',
    path: ['validTo'],
  });

/**
 * Save the caller's own profile. `version` is required (409 `stale_version` when stale) and every
 * save bumps it. A field left out is unchanged; an explicit `null` on `nameAsOnId`,
 * `passportNumber`, `passportCountry`, `passportExpiry`, `mealPreference` or `seatPreference`
 * clears it. The passport number is encrypted with the person's DEK and indexed by a blind index
 * (invariant 7) and never appears in a response. `frequentFlyer` and `visas` replace their lists
 * (see `updateTravellerVisaInputSchema`).
 */
export const updateSelfTravellerProfileBodySchema = z.object({
  version: travelVersionSchema,
  nameAsOnId: z.string().trim().min(1).max(120).nullable().optional(),
  passportNumber: z.string().trim().min(5).max(30).nullable().optional(),
  passportCountry: z.string().trim().length(2).nullable().optional(),
  passportExpiry: dateOnlySchema.nullable().optional(),
  mealPreference: z.string().trim().max(50).nullable().optional(),
  seatPreference: z.string().trim().max(50).nullable().optional(),
  frequentFlyer: z.array(travelFrequentFlyerSchema).max(10).optional(),
  visas: z.array(updateTravellerVisaInputSchema).max(20).optional(),
});

export const travelRequestContract = c.router(
  {
    listRequests: {
      method: 'GET',
      path: '/',
      query: listTravelRequestsQuerySchema,
      responses: {
        200: paginatedSchema(travelRequestSchema),
        401: errorBodySchema,
      },
      summary: 'List travel requests in the callers visible scope',
      metadata: routeMeta(null),
    },

    /** Mirrors `expense-multiline-report.contract.ts`'s own `listReportApprovals` exactly — every
     * request with a `WorkflowStep` currently awaiting THIS caller's decision, plus (for anyone
     * holding `workflow:manage` org-wide) every other pending/rejected request, visibility-only. */
    listRequestApprovals: {
      method: 'GET',
      path: '/approvals',
      query: paginationQuerySchema,
      responses: {
        200: paginatedSchema(travelRequestSchema),
        401: errorBodySchema,
      },
      summary: "List travel requests with a step currently awaiting the caller's decision",
      metadata: routeMeta(null),
    },

    /**
     * The policy in force for the traveller on the trip's start date, with their grade, currency and
     * timezone: what the New Trip form's live check runs. A STATIC segment declared before
     * `getRequest` so it outranks `/:travelRequestId`. `personId` defaults to the caller; someone
     * else's needs `travel:read` over them (403 otherwise).
     */
    getApplicablePolicy: {
      method: 'GET',
      path: '/policy/applicable',
      query: travelApplicablePolicyQuerySchema,
      responses: {
        200: travelApplicablePolicySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** `module_disabled`, or an unknown person. */
        404: errorBodySchema,
      },
      summary: "The travel policy, grade and timezone that apply to a traveller on a trip's start date",
      metadata: routeMeta('travel:request'),
    },

    getRequest: {
      method: 'GET',
      path: '/:travelRequestId',
      pathParams: tripParamsSchema,
      responses: {
        200: travelRequestSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One travel request',
      metadata: routeMeta(null),
    },

    createRequest: {
      method: 'POST',
      path: '/',
      headers: idempotencyHeadersSchema,
      body: createTravelRequestBodySchema,
      responses: {
        201: travelRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** `module_disabled`. */
        404: errorBodySchema,
        /** `duplicate_trip`. */
        409: errorBodySchema,
        /** `end_before_start`, `invalid_co_traveller`, `invalid_leave_request`, `personal_day_outside_trip`. */
        422: errorBodySchema,
      },
      summary: 'Start a new draft travel request',
      metadata: routeMeta('travel:request'),
    },

    updateRequest: {
      method: 'PATCH',
      path: '/:travelRequestId',
      pathParams: tripParamsSchema,
      body: updateTravelRequestBodySchema,
      responses: {
        200: travelRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** Also `module_disabled`. */
        404: errorBodySchema,
        /** `stale_version`, `duplicate_trip`. */
        409: errorBodySchema,
        /** `end_before_start`, `invalid_co_traveller`, `invalid_leave_request`, `personal_day_outside_trip`. */
        422: errorBodySchema,
      },
      summary: 'Edit one of your own draft travel requests (sends the current version)',
      metadata: routeMeta('travel:request'),
    },

    deleteRequest: {
      method: 'DELETE',
      path: '/:travelRequestId',
      pathParams: tripParamsSchema,
      responses: {
        200: travelRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Delete one of your own draft travel requests',
      metadata: routeMeta('travel:request'),
    },

    submitRequest: {
      method: 'POST',
      path: '/:travelRequestId/submit',
      pathParams: tripParamsSchema,
      headers: idempotencyHeadersSchema,
      body: submitTravelRequestBodySchema,
      responses: {
        200: travelRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** `stale_version`, `illegal_trip_transition`, `duplicate_trip`. */
        409: errorBodySchema,
        /** `start_in_past`, `post_facto_not_started`, `post_facto_too_old`, `justification_required`, a hard policy block. */
        422: errorBodySchema,
      },
      summary: 'Submit a draft travel request and start its approval workflow',
      metadata: routeMeta('travel:request'),
    },

    withdrawRequest: {
      method: 'POST',
      path: '/:travelRequestId/withdraw',
      pathParams: tripParamsSchema,
      headers: idempotencyHeadersSchema,
      body: withdrawTravelRequestBodySchema,
      responses: {
        200: travelRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Withdraw a draft or pending travel request before it is decided',
      metadata: routeMeta('travel:request'),
    },

    approveRequest: {
      method: 'POST',
      path: '/:travelRequestId/approve',
      pathParams: tripParamsSchema,
      headers: idempotencyHeadersSchema,
      body: decideTravelRequestBodySchema,
      responses: {
        200: travelRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Approve the step currently awaiting the caller — or reopen a rejected request',
      metadata: routeMeta(null),
    },

    rejectRequest: {
      method: 'POST',
      path: '/:travelRequestId/reject',
      pathParams: tripParamsSchema,
      headers: idempotencyHeadersSchema,
      body: decideTravelRequestBodySchema,
      responses: {
        200: travelRequestSchema,
        401: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Reject the step currently awaiting the caller',
      metadata: routeMeta(null),
    },

    updateBooking: {
      method: 'POST',
      path: '/:travelRequestId/booking',
      pathParams: tripParamsSchema,
      headers: idempotencyHeadersSchema,
      body: updateTravelBookingBodySchema,
      responses: {
        200: travelRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not yet `approved` — nothing to book. */
        409: errorBodySchema,
      },
      summary: "The travel desk half: record a trip's booking status",
      metadata: routeMeta('travel:approve'),
    },

    askQuestion: {
      method: 'POST',
      path: '/:travelRequestId/ask-question',
      pathParams: tripParamsSchema,
      headers: idempotencyHeadersSchema,
      body: z.object({
        question: z.string().trim().min(1).max(1000),
      }),
      responses: {
        200: travelRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Approver asks a question on a pending request, pausing its SLA',
      metadata: routeMeta(null),
    },

    answerQuestion: {
      method: 'POST',
      path: '/:travelRequestId/answer-question',
      pathParams: tripParamsSchema,
      headers: idempotencyHeadersSchema,
      body: z.object({
        answer: z.string().trim().min(1).max(2000),
      }),
      responses: {
        200: travelRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Requester answers an outstanding question on their request, resuming its SLA',
      metadata: routeMeta(null),
    },

    acceptInvite: {
      method: 'POST',
      path: '/:travelRequestId/accept-invite',
      pathParams: tripParamsSchema,
      headers: idempotencyHeadersSchema,
      body: z.object({}),
      responses: {
        200: travelRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Accept an invitation to join a group trip (moves invited -> draft)',
      // The handler also requires the caller to be the invited traveller (a 404 otherwise).
      metadata: routeMeta('travel:request'),
    },

    declineInvite: {
      method: 'POST',
      path: '/:travelRequestId/decline-invite',
      pathParams: tripParamsSchema,
      headers: idempotencyHeadersSchema,
      body: z.object({
        reason: z.string().trim().max(500).optional(),
      }),
      responses: {
        200: travelRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Decline an invitation to join a group trip (moves invited -> withdrawn)',
      metadata: routeMeta('travel:request'),
    },

    // ---- The traveller's side of the desk ------------------------------------------------------

    listTripBookings: {
      method: 'GET',
      path: '/:travelRequestId/bookings',
      pathParams: tripParamsSchema,
      responses: {
        200: z.array(tripBookingSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        /** `module_disabled`, or the trip is not the caller's to read. */
        404: errorBodySchema,
      },
      summary: "The trip's bookings as the traveller sees them, with any options the desk shared",
      // Signed-in only: the traveller and the people who decide the trip read it. The handler
      // refuses everyone else with a 404 (it also keeps the invitee's inviter out).
      metadata: routeMeta(null),
    },

    selectOption: {
      method: 'POST',
      path: '/:travelRequestId/bookings/:bookingId/options/:optionId/select',
      pathParams: optionParamsSchema,
      headers: idempotencyHeadersSchema,
      body: selectTravelOptionBodySchema,
      responses: {
        200: tripBookingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** `module_disabled`, or the booking or option is not the caller's. */
        404: errorBodySchema,
        /** `stale_version`, `booking_not_open`, `option_not_selected` (the option expired). */
        409: errorBodySchema,
      },
      summary: 'Pick one of the options the travel desk shared for a booking',
      metadata: routeMeta('travel:request'),
    },

    resolveItem: {
      method: 'POST',
      path: '/:travelRequestId/bookings/:bookingId/resolve',
      pathParams: bookingParamsSchema,
      headers: idempotencyHeadersSchema,
      body: resolveTravelItemBodySchema,
      responses: {
        200: tripBookingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** `stale_version`, `booking_not_open` (only a declined item or one whose options all lapsed). */
        409: errorBodySchema,
        /** `self_booking_not_allowed`. */
        422: errorBodySchema,
      },
      summary: 'Drop an item the desk could not book, or take it on to book yourself',
      metadata: routeMeta('travel:request'),
    },

    requestAmendment: {
      method: 'POST',
      path: '/:travelRequestId/amendments',
      pathParams: tripParamsSchema,
      headers: idempotencyHeadersSchema,
      body: raiseTravelAmendmentBodySchema,
      responses: {
        /** `status` is `pending` while an approver decides, `approved` when the change needed no approval and was applied. */
        201: travelAmendmentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** `stale_version`, `trip_not_open`, `amendment_pending`. */
        409: errorBodySchema,
        /** `unknown_itinerary_item`, `bad_end_date`, `empty_proposal`. */
        422: errorBodySchema,
      },
      summary: 'Ask to change an approved trip: dates, route, shorten, extend',
      metadata: routeMeta('travel:request'),
    },

    listAmendments: {
      method: 'GET',
      path: '/:travelRequestId/amendments',
      pathParams: tripParamsSchema,
      responses: {
        200: z.array(travelAmendmentSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "A trip's change requests, newest first",
      // The traveller and the people who decide the trip, the same rule as `getRequest`.
      metadata: routeMeta(null),
    },

    withdrawAmendment: {
      method: 'POST',
      path: '/:travelRequestId/amendments/:amendmentId/withdraw',
      pathParams: amendmentParamsSchema,
      headers: idempotencyHeadersSchema,
      body: z.object({}),
      responses: {
        200: travelAmendmentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** `amendment_not_pending`. */
        409: errorBodySchema,
      },
      summary: 'Pull back a change request that is still waiting for a decision',
      metadata: routeMeta('travel:request'),
    },

    decideAmendment: {
      method: 'POST',
      path: '/:travelRequestId/amendments/:amendmentId/decide',
      pathParams: amendmentParamsSchema,
      headers: idempotencyHeadersSchema,
      body: decideTravelAmendmentBodySchema,
      responses: {
        /** `status` stays `pending` when another approval step follows. */
        200: travelAmendmentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** Not the assigned approver and no `travel:approve` over the traveller. */
        404: errorBodySchema,
        /** `amendment_not_pending`. */
        409: errorBodySchema,
        /** No approval step is awaiting a decision. */
        412: errorBodySchema,
      },
      summary: 'Approve or reject a change request awaiting the caller',
      // Like `approveRequest`: the assigned approver may hold no travel permission, so the gate is
      // signed-in only and the handler checks the assignment or `travel:approve` over the traveller.
      metadata: routeMeta(null),
    },

    cancelTrip: {
      method: 'POST',
      path: '/:travelRequestId/cancel',
      pathParams: tripParamsSchema,
      headers: idempotencyHeadersSchema,
      body: cancelTravelTripBodySchema,
      responses: {
        /** The `cancel` amendment: `approved` once applied (the trip is then `cancelled`), `pending` while approval is awaited. */
        200: travelAmendmentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** `stale_version`, `trip_not_open` (use withdraw for a draft or pending trip), `amendment_pending`. */
        409: errorBodySchema,
      },
      summary: 'Cancel an approved or booked trip (a draft or pending one is withdrawn instead)',
      metadata: routeMeta('travel:request'),
    },

    getTimeline: {
      method: 'GET',
      path: '/:travelRequestId/timeline',
      pathParams: tripParamsSchema,
      responses: {
        /** Oldest first; desk-internal events are left out. */
        200: z.array(travelTimelineEventSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "A trip's history as the traveller may see it",
      metadata: routeMeta(null),
    },

    getGroupRoster: {
      method: 'GET',
      path: '/:travelRequestId/group',
      pathParams: tripParamsSchema,
      responses: {
        200: travelGroupRosterSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** Not travelling in this group. */
        404: errorBodySchema,
      },
      summary: "The people travelling in the same group, with each one's booking progress",
      // Any member of the group reads it; the handler checks the caller is one of the travellers.
      metadata: routeMeta(null),
    },

    listTripDocuments: {
      method: 'GET',
      path: '/:travelRequestId/documents',
      pathParams: tripParamsSchema,
      responses: {
        200: z.array(tripDocumentSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "The tickets, vouchers and invoices the desk attached to the caller's own trip",
      metadata: routeMeta('travel:request'),
    },

    downloadTripDocument: {
      method: 'GET',
      path: '/:travelRequestId/documents/:documentId',
      pathParams: documentParamsSchema,
      responses: {
        200: travelTripDocumentDownloadSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Download one document of the caller's own trip; access is re-checked on every fetch",
      // The body is file bytes: never pulled into a prompt.
      metadata: routeMeta('travel:request', { ai: 'hidden' }),
    },

    getProfile: {
      method: 'GET',
      path: '/profile/me',
      responses: {
        200: travellerProfileViewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** `module_disabled`. */
        404: errorBodySchema,
      },
      summary: "Get current user's traveller profile with masked passport and visa numbers",
      // ai hidden: the profile is identity data (name as on ID, passport country and expiry, visas).
      metadata: routeMeta('travel:request', { ai: 'hidden' }),
    },

    updateProfile: {
      method: 'PUT',
      path: '/profile/me',
      // No `Idempotency-Key` (run 7, F12): the idempotency store would keep the response, which is
      // passport-derived, outside the crypto lib. A PUT of the whole profile is idempotent by
      // itself, and `version` (409 `stale_version`) refuses a replay that lost a race.
      body: updateSelfTravellerProfileBodySchema,
      responses: {
        200: travellerProfileViewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** `module_disabled`. */
        404: errorBodySchema,
        /** `stale_version`. */
        409: errorBodySchema,
        /** `visa_not_found`, or a value the schema refuses. */
        422: errorBodySchema,
      },
      summary: "Update current user's traveller profile with encrypted passport and visa numbers",
      // ai hidden: the request body carries the plaintext passport and visa numbers, which must
      // never pass through a model or be stored in an `ai_proposals` row (invariants 7 and 9).
      metadata: routeMeta('travel:request', { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/travel-requests' },
);

export type TravelRequest = z.infer<typeof travelRequestSchema>;
